import { Schema } from "effect";
import {
  GitHubPullRequestNumberSchema,
  GitHubRepositoryNameSchema,
  GitHubRevisionSchema,
} from "./desktop-github";
import { DesktopGitRelativePathSchema } from "./desktop-git";
import {
  CanonicalIdentifierSchema,
  NonNegativeSafeIntegerSchema,
  PapersFlowBoundaryParseOptions,
  toPapersFlowValidationSchema,
} from "./standard";
import { utf8ByteLength } from "./utf8";

export const REVIEW_COMMENT_MESSAGE_MAX_ATTACHMENTS = 12;
export const REVIEW_COMMENT_MAX_TEXT_BYTES = 4 * 1_024;
export const REVIEW_COMMENT_MAX_EXCERPT_BYTES = 12 * 1_024;
export const REVIEW_COMMENT_MESSAGE_MAX_TOTAL_BYTES = 128 * 1_024;

const PositiveLineNumberSchema = NonNegativeSafeIntegerSchema.check(
  Schema.isGreaterThanOrEqualTo(1),
);
const BoundedReviewTextSchema = Schema.String.check(
  Schema.makeFilter(
    (value) =>
      (value === value.trim() &&
        value.length > 0 &&
        utf8ByteLength(value) <= REVIEW_COMMENT_MAX_TEXT_BYTES) ||
      `must be trimmed, non-empty, and at most ${REVIEW_COMMENT_MAX_TEXT_BYTES} UTF-8 bytes`,
  ),
);
const BoundedReviewExcerptSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.makeFilter(
    (value) =>
      utf8ByteLength(value) <= REVIEW_COMMENT_MAX_EXCERPT_BYTES ||
      `must be at most ${REVIEW_COMMENT_MAX_EXCERPT_BYTES} UTF-8 bytes`,
  ),
);
const ReviewLabelSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(120),
  Schema.makeFilter((value) => value === value.trim() || "must not have surrounding whitespace"),
);
export const ReviewCommentSourceSchema = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("local-working-tree"),
  }),
  Schema.Struct({
    kind: Schema.Literal("github-pull-request"),
    repository: GitHubRepositoryNameSchema,
    pullRequestNumber: GitHubPullRequestNumberSchema,
    headRevision: GitHubRevisionSchema,
  }),
]);

export const ReviewCommentLineRangeSchema = Schema.Struct({
  startLine: PositiveLineNumberSchema,
  startSide: Schema.Literals(["additions", "deletions"]),
  endLine: PositiveLineNumberSchema,
  endSide: Schema.Literals(["additions", "deletions"]),
  label: ReviewLabelSchema,
});

/** Durable, capability-free context selected by a user from a code-review surface. */
export const ReviewCommentSchema = Schema.Struct({
  version: Schema.Literal("review-comment/v1"),
  commentId: CanonicalIdentifierSchema,
  source: ReviewCommentSourceSchema,
  path: DesktopGitRelativePathSchema,
  range: ReviewCommentLineRangeSchema,
  comment: BoundedReviewTextSchema,
  excerpt: BoundedReviewExcerptSchema,
  excerptTruncated: Schema.Boolean,
});

export const ReviewCommentStandardSchema = toPapersFlowValidationSchema(ReviewCommentSchema);

export const decodeReviewComment = Schema.decodeUnknownSync(
  ReviewCommentSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeReviewComment = Schema.encodeSync(
  ReviewCommentSchema,
  PapersFlowBoundaryParseOptions,
);

export function reviewCommentContextBytes(comment: ReviewComment): number {
  return utf8ByteLength(comment.comment) + utf8ByteLength(comment.excerpt);
}

export type ReviewCommentSource = typeof ReviewCommentSourceSchema.Type;
export type ReviewCommentSourceEncoded = typeof ReviewCommentSourceSchema.Encoded;
export type ReviewCommentLineRange = typeof ReviewCommentLineRangeSchema.Type;
export type ReviewCommentLineRangeEncoded = typeof ReviewCommentLineRangeSchema.Encoded;
export type ReviewComment = typeof ReviewCommentSchema.Type;
export type ReviewCommentEncoded = typeof ReviewCommentSchema.Encoded;
